import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { GoogleMapComponent } from '../../../../core/components/google-maps/google-maps.component';
import { LogotypeComponent } from '../../../../core/components/logotype/logotype.component';
import { IAsyncState } from '../../../../core/interfaces/async-state.interface';
import { AlertService } from '../../../../core/services/alert.service';
import { ConfirmationService } from '../../../../core/services/confirmation.service';
import { GoogleMapsService } from '../../../../core/services/google-maps-loader.service';
import { ShippingStatusEnum } from '../../enums/shipping-status.enum';
import { ShippingStopKindEnum } from '../../enums/shipping-stop-kind.enum';
import { IShippingFailure } from '../../interfaces/shipping-failure.interface';
import { IShippingStop } from '../../interfaces/shipping-stop.interface';
import { IShipping } from '../../interfaces/shipping.interface';
import { ShippingService } from '../../services/shipping.service';
import { ShippingFailedComponent } from '../shipping-failed/shipping-failed.component';
import { ShippingLoaderComponent } from '../shipping-loader/shipping-loader.component';
import { ShippingStopComponent } from '../shipping-stop/shipping-stop.component';

@Component({
  selector: 'peca-shipping-details',
  templateUrl: './shipping-details.component.html',
  styleUrl: './shipping-details.component.scss',
  imports: [AsyncPipe, LogotypeComponent, GoogleMapComponent, ShippingStopComponent, ShippingLoaderComponent, ShippingFailedComponent],
})
export class ShippingDetailsComponent {
  @Input()
  shipping!: IShipping;

  @Output()
  reload = new EventEmitter<IShipping>();

  googleMapsService = inject(GoogleMapsService);
  shippingService = inject(ShippingService);
  alertService = inject(AlertService);
  confirmationService = inject(ConfirmationService);
  activatedRoute = inject(ActivatedRoute);
  shippingFinalizingState$ = new BehaviorSubject<IAsyncState<any>>({ loading: false });
  finalizeFailure: IShippingFailure | null = null;
  private pendingStop: IShippingStop | null = null;
  private pendingStatus: { orderId: string; status: ShippingStatusEnum } | null = null;

  get shippingReferenceSnapshot() {
    return this.activatedRoute.snapshot.paramMap.get('shippingReference') as string;
  }

  onGoogleMapsLoad() {
    this.googleMapsService.stops.set(
      this.shipping.stops.map((stop, index) => ({
        id: index + 1,
        position: { lat: stop.latitude, lng: stop.longitude },
        name: stop.kind.toString().toLowerCase().includes('pickup') ? 'Coleta' : 'Entrega',
        address: stop.address,
        phone: stop.phone,
        finalized: stop.finalized,
      }))
    );
  }

  onClickFinalizeStop(stop: IShippingStop) {
    if (stop.kind === ShippingStopKindEnum.DROPOFF && !this.canDropoff(stop.commerceReferences, this.shipping.stops)) {
      this.alertService.confirm(['Você não pode realizar a entrega de um pedido que não foi coletado.']);
      return;
    }

    if (!stop.proofUploadId) {
      this.alertService.confirm(['A foto do comprovante é obrigatória para concluir.']);
      return;
    }

    stop.finalized = true;
    this.pendingStop = stop;
    this.pendingStatus = null;
    this.finalizeFailure = null;

    this.shippingService.finalizeStop(
      this.shippingReferenceSnapshot,
      stop,
      { uploadId: stop.proofUploadId, receiverName: stop.receiverName },
    ).subscribe({
      next: () => {
        this.pendingStop = null;
        if (!this.hasStopsRemaining()) {
          this.reload.emit();
        }

        const currentStops = this.googleMapsService.stops();
        const updatedStops = currentStops.map((s) => (s.id === stop.index + 1 ? { ...s, finalized: true } : s));

        this.googleMapsService.stops.set(updatedStops);
      },
      error: (error) => {
        stop.finalized = false;
        this.finalizeFailure = {
          title: 'Falha ao finalizar a parada',
          description: this.readErrorMessage(error),
        };
        this.shippingFinalizingState$.next({ failed: true });
      },
    });
  }

  onRetry() {
    if (this.pendingStop) {
      const stop = this.pendingStop;
      this.pendingStop = null;
      this.shippingFinalizingState$.next({ loading: false });
      this.onClickFinalizeStop(stop);
      return;
    }
    if (this.pendingStatus) {
      const { orderId, status } = this.pendingStatus;
      this.updateShippingStatus(orderId, status);
      return;
    }
    this.reload.emit(this.shipping);
  }

  private updateShippingStatus(orderId: string, status: ShippingStatusEnum) {
    this.shippingFinalizingState$.next({ loading: true });
    this.pendingStatus = { orderId, status };
    this.pendingStop = null;
    this.shippingService.webhook({ orderId, status }, this.shippingReferenceSnapshot).subscribe({
      next: () => {
        this.pendingStatus = null;
        this.reload.emit(this.shipping);
      },
      error: (error) => {
        this.finalizeFailure = {
          title: 'Falha ao atualizar a entrega',
          description: this.readErrorMessage(error),
        };
        this.shippingFinalizingState$.next({ failed: true });
      },
    });
  }

  private readErrorMessage(error: unknown): string {
    const fallback = 'Não foi possível concluir. Tente novamente e caso o problema persistir, por favor, entre em contato com o suporte.';
    if (error instanceof HttpErrorResponse) {
      const message = (error.error as { message?: string | string[] } | null)?.message ?? error.message;
      if (Array.isArray(message) && message.length > 0) {
        return message.join(' ');
      }
      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }
    }
    return fallback;
  }

  private canDropoff(commerceReferences: string[], stops: IShippingStop[]): boolean {
    const pickups = stops?.filter((x) => x.kind === 'pickup' && x.commerceReferences.some((c) => commerceReferences.includes(c)));
    return pickups.every((p) => p.finalized);
  }

  private hasStopsRemaining() {
    const hasStopsRemaining = this.shipping.stops.some((stop) => !stop.finalized);
    return hasStopsRemaining;
  }
}
